import {
    Component,
    OnInit,
    ChangeDetectionStrategy,
    ViewChild,
    ElementRef,
    ChangeDetectorRef,
    AfterViewInit,
    OnDestroy
} from '@angular/core';
import {BsModalRef, BsModalService} from 'ngx-bootstrap';
import {BsModalEventsEmitter} from 'src/app/modules/shared/events/bsModal.events';
import {Subscription} from 'rxjs';
import {DiaryService} from 'src/app/modules/shared/services/diary.service';
import {DiaryRecipeViewModel} from 'src/app/modules/shared/viewModels/diaryRecipe.viewModel';
import {RecipeViewModel} from 'src/app/modules/shared/viewModels/recipe.viewModel';
import {filter} from 'rxjs/operators';
import {MealRecipeViewModel} from 'src/app/modules/shared/viewModels/mealRecipe.viewModel';
import {ProductsService} from 'src/app/modules/shared/services/products.service';
import {ProductViewModel} from 'src/app/modules/shared/viewModels/product.viewModel';
import {MealRecipeProductViewModel} from 'src/app/modules/shared/viewModels/mealRecipeProduct.viewModel';
import {LocalisationService} from 'src/app/modules/shared/services/localisation.service';
import {DiaryRecipeProductViewModel} from 'src/app/modules/shared/viewModels/diaryRecipeProduct.viewModel';
import {MealsComponent} from '../../meals/meals.component';
import {MealViewModel} from 'src/app/modules/shared/viewModels/meal.viewModel';
import {CreateDiaryDTO} from '../../../../shared/dto/createDiary.dto';
import {Constants} from '../../../../shared/config/constants.config';
import {ImageViewModel} from '../../viewModels/image.viewModel';
import {PageService} from '../../../../shared/services/page.service';
import {sum_products_weight, sum_diary_products_weight} from '../../../../shared/utilities/recipe.utilities';
import * as moment from 'moment';
import {BaseUserViewModel} from '../../../../shared/viewModels/userFriend.viewModel';
import {FriendsService} from '../../../../shared/services/friends.service';
import {DomSanitizer} from '@angular/platform-browser';
import {ChangeDiaryRecipeProductComponent} from '../change-diary-recipe-product/change-diary-recipe-product.component';
import {AppLoadingEventsEmitter} from '../../../../shared/events/loading.events';

export class FriendViewModel {
    id?: number;
    friend?: BaseUserViewModel;
    color?: string;
    products?: DiaryRecipeProductViewModel[];
}

@Component({
    selector: 'app-view-recipe',
    templateUrl: './view-recipe.component.html',
    styleUrls: ['./view-recipe.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ViewRecipeComponent implements OnInit, AfterViewInit, OnDestroy {
    private readonly mobileWidth: number = 576;

    @ViewChild('wrapper') wrapper: ElementRef<HTMLElement>;

    mealsComponent: MealsComponent;
    meal: MealViewModel;
    loading = true;
    recipe: RecipeViewModel;
    recipeImages: ImageViewModel[];
    diaryRecipe: DiaryRecipeViewModel;
    mealRecipe: MealRecipeViewModel;
    products: ProductViewModel[];
    productCalculationsByProductId: { [key: number]: MealRecipeProductViewModel | DiaryRecipeProductViewModel } = {};
    addRecipeModalRef: BsModalRef;
    subscriptions: Subscription[] = [];

    showMacros = true;

    showFriends = false;
    friends: { [key: number]: FriendViewModel } = {};
    friendColors = ['#3e3ed2', '#d28d0e', 'purple', 'red', 'grey'];

    recipeEmbedCode: string = null;
    showHowToCook = false;

    changeDiaryRecipeProductBsModalRef: BsModalRef;

    constructor(
        public readonly bsModalRef: BsModalRef,
        private readonly bsModalEventsEmitter: BsModalEventsEmitter,
        private readonly diaryService: DiaryService,
        private readonly productsService: ProductsService,
        private readonly pageService: PageService,
        public readonly changeDetectorRef: ChangeDetectorRef,
        private readonly localisationService: LocalisationService,
        private readonly friendsService: FriendsService,
        readonly constants: Constants,
        private readonly sanitizer: DomSanitizer,
        private readonly bsModalService: BsModalService,
        private readonly appLoadingEventsEmitter: AppLoadingEventsEmitter,
    ) {
    }

    ngOnInit() {
        this.subscriptions.push(
            this.bsModalEventsEmitter.bsModalStateChanged$.pipe(filter(bsModalRef => bsModalRef === this.bsModalRef)).subscribe(() => {
                this.init();
            })
        );
    }

    ngAfterViewInit() {
        if (window.innerWidth <= this.mobileWidth) {
            this.toFullscreen();
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    round(number: number): number {
        return Math.round(number);
    }

    onAdd() {
        const addRecipeToDiary = () => {
            this.diaryService
                .addRecipeToDiary({
                    date: this.mealsComponent.date,
                    meal: this.meal.id,
                    recipe: this.mealRecipe.recipe.id,
                    products: this.mealRecipe.products
                })
                .subscribe(() => {
                    this.clearShoppingList().subscribe(response => {
                        this.bsModalRef.hide();
                        this.addRecipeModalRef.hide();

                        this.mealsComponent.allowSwipingToNotEatenMeal = false;
                        this.mealsComponent.init().subscribe(() => {
                            this.mealsComponent.allowSwipingToNotEatenMeal = true;
                        });
                    });
                });
        };

        if (this.mealRecipe) {
            if (!this.mealsComponent.diary) {
                this.createDiary().subscribe(() => {
                    addRecipeToDiary();
                });
            } else {
                addRecipeToDiary();
            }
        }
    }

    initProductCalculationsByProductId() {
        if (this.diaryRecipe) {
            this.productCalculationsByProductId = this.diaryRecipe.diary_products.reduce((p: object, c: DiaryRecipeProductViewModel) => {
                p[c.product.id] = c;

                return p;
            }, {});
        } else if (this.mealRecipe) {
            this.productCalculationsByProductId = this.mealRecipe.products.reduce((p: object, c: MealRecipeProductViewModel) => {
                p[c.product_id] = c;

                return p;
            }, {});
        }
    }

    public init() {
        const callback = (products: ProductViewModel[]) => {
            this.products = products;
            this.initProductCalculationsByProductId();
            this.initUserFriends();

            this.loading = false;
            this.changeDetectorRef.markForCheck();
        };

        let recipeId, productIds, diaryRecipeId;

        if (this.diaryRecipe) {
            diaryRecipeId = this.diaryRecipe.id;
            recipeId = this.diaryRecipe.recipe.id;
            productIds = this.diaryRecipe.diary_products.map(p => p.product.id);
        } else if (this.mealRecipe) {
            diaryRecipeId = 0;
            recipeId = this.mealRecipe.recipe.id;
            productIds = this.mealRecipe.products.map(p => p.product_id);
        }

        this.subscriptions.push(
            this.diaryService.getRecipe(recipeId, diaryRecipeId).subscribe(recipe => {
                this.recipe = recipe.recipe;
                this.recipeImages = recipe.images;

                if (this.recipe.youtube_link) {
                    this.recipeEmbedCode = this.youtubeParser(this.recipe.youtube_link);
                }

                this.subscriptions.push(
                    this.productsService
                        .getProductsByIds({
                            ids: productIds
                        })
                        .subscribe(products => {
                            callback(products);
                        })
                );
            })
        );
    }

    private createDiary(
        createDiaryDTO: CreateDiaryDTO = {
            daily_meals: this.mealsComponent.userDailyMeals.id,
            diet: this.mealsComponent.userProfile.diet ? this.mealsComponent.userProfile.diet.id : null
        }
    ) {
        return this.diaryService.createDiary(this.mealsComponent.date, createDiaryDTO);
    }

    private toFullscreen() {
        const modalDialog = this.wrapper.nativeElement.parentElement.parentElement.parentElement;
        const modalContent = this.wrapper.nativeElement.parentElement.parentElement;
        const modal = this.wrapper.nativeElement.parentElement;

        modalDialog.style.borderRadius = '0';
        modalDialog.style.display = 'flex';
        modalDialog.style.margin = '0';
        modalDialog.style.padding = '0';
        modalDialog.style.width = '100%';
        modalDialog.style.maxWidth = 'unset';
        modalDialog.style.minHeight = '100%';
        modalDialog.style.height = '100%';
        modalContent.style.borderRadius = '0';
        modal.style.display = 'block';
        modal.style.height = '100%';
    }

    sumWeight(products: any[], productCalculationsByProductId): number {
        return sum_products_weight(products, productCalculationsByProductId);
    }

    sum_diary_products_weight(products: DiaryRecipeProductViewModel[]): number {
        return sum_diary_products_weight(products);
    }

    getShortName(first_name: string, last_name: string) {
        if (first_name.length <= 0) {
            return last_name.toUpperCase();
        }

        return first_name[0].toUpperCase() + '. ' + last_name.charAt(0).toUpperCase() + last_name.toLowerCase().slice(1);
    }

    private clearShoppingList() {
        const date = new Date();
        const monday = new Date(date.setDate((date.getDate() - date.getDay() + (date.getDay() === 0 ? -6 : 1))));
        const sunday = new Date(date.setDate(date.getDate() - date.getDay() + ((date.getDay() === 0 ? -6 : 1)) + 6));

        return this.diaryService.clearShoppingItems(moment(monday).format('YYYY-MM-DD'), moment(sunday).format('YYYY-MM-DD'));
    }

    private initUserFriends() {
        this.friendsService.getFriends().subscribe(friends => {
            friends.map(friend => {
                this.friends[friend.id] = {
                    id: friend.id,
                    friend: friend.friend,
                    color: this.friendColors[0]
                };
                this.friendColors.shift();
            });

            this.changeDetectorRef.markForCheck();
            this.checkForSameFriendsRecipe();
        });
    }

    private checkForSameFriendsRecipe() {
        let mealId = 0;

        if (this.diaryRecipe) {
            mealId = this.diaryRecipe.meal.id;
        } else if (this.mealRecipe) {
            mealId = this.meal.id;
        }

        this.diaryService.getFriendsDiaryRecipes({
            recipe_id: this.recipe.id,
            meal_id: mealId,
            date: this.mealsComponent.date
        }).subscribe(data => {
            Object.keys(this.friends).map(friendId => {
                if (typeof data[this.friends[friendId].friend.id] !== 'undefined') {
                    this.friends[friendId].products = data[this.friends[friendId].friend.id]['diary_products'];
                }
            });

            Object.keys(this.friends).map(friendId => {
                if (!this.friends[friendId].products) {
                    delete this.friends[friendId];
                }
            });

            if (Object.keys(data).length > 0) {
                this.showFriends = true;
            }

            this.changeDetectorRef.markForCheck();
        });
    }

    onlyKeys(data: any) {
        return Object.keys(data);
    }

    floor(number: number) {
        return Math.floor(number);
    }

    private youtubeParser(url) {
        const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
        const match = url.match(regExp);
        return (match && match[7].length === 11) ? match[7] : false;
    }

    private getSvg(svg: string): string {
        // if (this.themeService.getCurrentTheme() !== 'light') {
        svg += '-white';
        // }

        return `assets/images/macros/${svg}.svg`;
    }

    openChangeDiaryRecipeProduct(productId) {
        // if (blockRecipeOpenAndOpenUpgradeModal === true) {
        //     this.openUpgradeModal();
        //     return;
        // }

        let diary_recipe_product_id = 0;

        this.diaryRecipe.diary_products.forEach(diary_product => {
            if (diary_product.product.id === productId) {
                diary_recipe_product_id = diary_product.id;
            }
        });

        this.changeDiaryRecipeProductBsModalRef = this.bsModalService.show(ChangeDiaryRecipeProductComponent, {
            backdrop: true
        });

        this.changeDiaryRecipeProductBsModalRef.content.viewRecipeComponent = this;
        this.changeDiaryRecipeProductBsModalRef.content.mealsComponent = this.mealsComponent;
        this.changeDiaryRecipeProductBsModalRef.content.diaryRecipe = this.diaryRecipe;
        this.changeDiaryRecipeProductBsModalRef.content.diaryRecipeProductId = diary_recipe_product_id;

        this.bsModalEventsEmitter.broadcastBsModalStateChanged(this.changeDiaryRecipeProductBsModalRef);
    }

    deleteProductFromDiaryRecipe(productId: number) {
        if (confirm(this.localisationService.localise('confirm_delete_product'))) {
            this.appLoadingEventsEmitter.broadcastAppIsLoading(true);

            this.diaryService
                .removeProductFromDiaryRecipe(this.mealsComponent.date, productId)
                .subscribe(data => {
                    this.appLoadingEventsEmitter.broadcastAppIsLoading(false);

                    this.diaryRecipe = data['diary_recipe'];
                    this.changeDetectorRef.detectChanges();

                    this.showMacros = false;
                    this.changeDetectorRef.detectChanges();

                    this.showMacros = true;
                    this.changeDetectorRef.detectChanges();

                    this.init();
                    this.changeDetectorRef.detectChanges();

                    this.mealsComponent.init().subscribe(() => {

                    });
                }, () => {
                    this.appLoadingEventsEmitter.broadcastAppIsLoading(false);

                });
        }
    }

    isIOS() {
        return [
                'iPad Simulator',
                'iPhone Simulator',
                'iPod Simulator',
                'iPad',
                'iPhone',
                'iPod'
            ].includes(navigator.platform)
            // iPad on iOS 13 detection
            || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
    }
}
